'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CertificateForm, type CertificateFormRef } from '@/components/partials/certificates'
import { ContentError, FormSkeleton } from '@/components/partials'
import { type CreateCertificateParams } from '@/types/certificate'
import { useCertificate, useUpdateCertificate } from '@/hooks'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const certificateFormRef = useRef<CertificateFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: certificate, isLoading, error } = useCertificate(id)
  const { mutate: updateCertificate, isPending } = useUpdateCertificate()

  const handleSubmit = (data: CreateCertificateParams) => {
    updateCertificate(
      { id, params: data },
      {
        onSuccess: async updatedCertificate => {
          setIsRedirecting(true)
          queryClient.setQueryData(['certificate', id], updatedCertificate)
          await queryClient.invalidateQueries({ queryKey: ['certificates'] })
          toast.success(t('updated_successfully'))
          router.push(`/galleries/certificates/${updatedCertificate.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (certificateFormRef.current) {
        certificateFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/galleries/certificates/${id}`)
  }

  if (isLoading || !certificate) {
    return <FormSkeleton fieldRows={1} showPictures />
  }

  if (error || !certificate) {
    return <ContentError error={error} listUrl="/galleries/certificates" />
  }

  return (
    <CertificateForm
      ref={certificateFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={certificate}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
